import { useCallback, useEffect, useState } from "react";
import { Box, TextField, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { EQUIPMENT_GROUPS_ENDPOINT, EQUIPMENT_MODELS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import CustomAutocomplete from "../ui/customAutocomplete/customAutocomplete.component";

interface EquipmentModel {
  id: string;
  name?: string;
  display_name?: string;
  [key: string]: any;
}

interface EquipmentGroupFormProps {
  id?: string;
  onSuccess?: () => void;
}

export default function EquipmentGroupForm({ id, onSuccess }: EquipmentGroupFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(EQUIPMENT_GROUPS_ENDPOINT);
  const [name, setName] = useState("");
  const [equipmentModels, setEquipmentModels] = useState<EquipmentModel[]>([]);
  const [fetchedOptions, setFetchedOptions] = useState<EquipmentModel[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(!!id);

  useEffect(() => {
    if (!id) {
      setLoadingInitial(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const response = await get(id);
      if (cancelled) return;
      if (response?.data) {
        setName(response.data.name ?? "");
        setEquipmentModels(response.data.equipment_models ?? []);
      }
      setLoadingInitial(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, get]);

  const handleOptionsChange = useCallback((options: EquipmentModel[]) => {
    setFetchedOptions((prev) => {
      const merged = [...prev];
      options.forEach((option) => {
        if (!merged.find((existing) => String(existing.id) === String(option.id))) {
          merged.push(option);
        }
      });
      return merged;
    });
  }, []);

  const selectedOptions = [...equipmentModels, ...fetchedOptions].filter(
    (option, index, all) =>
      all.findIndex((item) => String(item.id) === String(option.id)) === index
  );

  const handleSubmit = async () => {
    const payload = {
      name,
      equipment_models: equipmentModels.map((equipmentModel) => equipmentModel.id),
    };

    if (id) {
      const result = await update({ id, ...payload });
      if (result?.success) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.();
        window.dispatchEvent(new Event("equipmentGroupUpdated"));
      } else {
        const msg =
          result?.errorData?.message ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    } else {
      const result = await create(payload);
      if (result?.data?.id) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.();
        window.dispatchEvent(new Event("equipmentGroupAdded"));
      } else {
        const err = result?.errorData;
        const msg =
          (err && typeof err === "object" && "message" in err && (err as { message?: string }).message) ||
          (err && typeof err === "string" ? err : undefined) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    }
  };

  if (loadingInitial) {
    return <CustomCircularProgress />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        size="small"
        label={t("Name")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
      />

      <CustomAutocomplete
        multiple
        size="small"
        apiConfig={{ url: EQUIPMENT_MODELS_ENDPOINT, minSearchChars: 0, debounceMs: 300 }}
        options={selectedOptions}
        onOptionsChange={handleOptionsChange}
        value={equipmentModels}
        onChange={(_, newValue) => setEquipmentModels((newValue as EquipmentModel[]) || [])}
        isOptionEqualToValue={(option, selectedValue) =>
          String((option as EquipmentModel).id) === String((selectedValue as EquipmentModel).id)
        }
        getOptionLabel={(option) =>
          (option as EquipmentModel).display_name || (option as EquipmentModel).name || ""
        }
        noOptionsText={t("No options")}
        renderInput={(params) => <TextField {...params} label={t("Equipment Models")} />}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          width: "100%",
        }}
      >
        <Button
          variant="contained"
          color="success"
          disabled={loading || !name}
          onClick={handleSubmit}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
